import { useQuery } from '@tanstack/react-query';
import { getSupabaseClient } from '../utils/supabaseClient';

/**
 * Fetches expenses with optional server-side filtering via RPC.
 */
// Exported so the CSV export can fetch the CURRENT FILTERS with no page cap, on demand.
// get_expenses_filtered ends `LIMIT $10` with the parameter passed straight through, so
// `limit: null` is genuinely unlimited here — verified live. Do NOT assume that across
// RPCs: the jobs one ends `LIMIT COALESCE(_limit, 50)`, where null means FIFTY.
export const fetchExpenses = async (companyId, filters = {}) => {
  if (!companyId) {
    throw new Error('Company ID is required');
  }

  const supabase = getSupabaseClient();

  // If filters provided, use the RPC function for server-side filtering
  if (Object.keys(filters).length > 0) {
    const {
      search = null,
      category = null,
      approvalStatus = null,
      entryType = null,
      reimbursableFilter = null,
      dateFrom = null,
      dateTo = null,
      sortBy = 'date',
      sortOrder = 'desc',
      limit = 50,
      offset = 0,
      exceptionFilter = null,
      vendorId = null
    } = filters;

    const { data, error } = await supabase.rpc('get_expenses_filtered', {
      _company_id: companyId,
      _search_term: search,
      _category_filter: category,
      _approval_status_filter: approvalStatus,
      _entry_type_filter: entryType,
      _reimbursable_filter: reimbursableFilter,
      _date_from: dateFrom,
      _date_to: dateTo,
      _sort_by: sortBy,
      _sort_order: sortOrder,
      _limit: limit,
      _offset: offset,
      // Server-side so the count and the rows come from the SAME predicate. This used
      // to be applied in the page over one 50-row page while the tiles counted every
      // row (F-ORION-0816-1850).
      _exception_filter: exceptionFilter,
      // #5585: a vendor's usage tile opens this list filtered to that vendor.
      _vendor_id: vendorId
    });

    if (error) {
      console.error('RPC error:', error);
      throw error;
    }

    // Handle the response structure: { expense: jsonb, total_count: bigint }
    const expenses = (data || []).map(row => row.expense);
    const totalCount = data?.[0]?.total_count ? Number(data[0].total_count) : expenses.length;

    // Sort receipts by sort_order for each expense
    const expensesWithSortedReceipts = expenses.map(expense => ({
      ...expense,
      expense_receipts: (expense.expense_receipts || []).sort(
        (a, b) => (a.sort_order || 0) - (b.sort_order || 0)
      )
    }));

    return {
      expenses: expensesWithSortedReceipts,
      totalCount
    };
  }

  // Fallback: Original behavior - fetch all expenses without filters
  const { data, error } = await supabase
    .from('expenses')
    .select(`
      *,
      work_orders!expenses_work_order_id_fkey (
        id,
        title
      ),
      expense_receipts (
        id,
        file_url,
        file_name,
        mime_type,
        sort_order,
        uploaded_at
      )
    `)
    .eq('company_id', companyId)
    .order('expense_date', { ascending: false });

  if (error) {
    throw error;
  }

  // Sort receipts by sort_order for each expense
  const expensesWithSortedReceipts = (data || []).map(expense => ({
    ...expense,
    expense_receipts: (expense.expense_receipts || []).sort(
      (a, b) => (a.sort_order || 0) - (b.sort_order || 0)
    )
  }));

  return {
    expenses: expensesWithSortedReceipts,
    totalCount: expensesWithSortedReceipts.length
  };
};

/**
 * #6057: narrow an expense list to one team member. expenses.employee_id is FK'd to users(id),
 * so this takes a users.id. Shared by My Dashboard's "Owed to me" tile and the Expenses page's
 * ?employee= filter, so the tile's count and the list it opens are one predicate.
 */
export const filterExpensesByEmployee = (expenses, userId) =>
  (expenses || []).filter((e) => !!userId && e?.employee_id === userId);

/**
 * React Query hook for fetching expenses.
 * @param {string} companyId - Company ID
 * @param {Object} filters - Optional filter parameters for server-side filtering
 * @param {string} filters.search - Search term (description, vendor)
 * @param {string} filters.category - Category filter
 * @param {string} filters.approvalStatus - Approval status filter
 * @param {string} filters.entryType - Entry type filter
 * @param {string} filters.reimbursableFilter - 'yes', 'no', or null
 * @param {string} filters.dateFrom - Start date
 * @param {string} filters.dateTo - End date
 * @param {string} filters.sortBy - Sort field: 'date', 'amount', 'category', 'status'
 * @param {string} filters.sortOrder - Sort order: 'asc', 'desc'
 * @param {number} filters.limit - Results per page
 * @param {number} filters.offset - Pagination offset
 */
export const useExpenses = (companyId, filters = {}) => {
  const hasFilters = Object.keys(filters).length > 0;
  
  return useQuery({
    queryKey: ['expenses', companyId, filters],
    queryFn: () => fetchExpenses(companyId, filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!companyId,
  });
};

/**
 * Fetches vendors for a company.
 */
const fetchVendors = async (companyId) => {
  if (!companyId) {
    throw new Error('Company ID is required');
  }

  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from('vendors')
    .select('*')
    .eq('company_id', companyId)
    .order('name', { ascending: true });

  if (error) {
    throw error;
  }

  return data || [];
};

/**
 * React Query hook for fetching vendors.
 */
export const useVendors = (companyId) => {
  return useQuery({
    queryKey: ['vendors', companyId],
    queryFn: () => fetchVendors(companyId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!companyId,
  });
};
